import { gql } from "graphql-request";
import type { GraphQLClient } from "graphql-request";
import type { KpiStatisticsData, ReportMetric, ReportMetricsData } from "@nexus/widgets";
import type { DataResolver, SourceSelection } from "@/lib/dashboard/resolverTypes";
import { getTenantIds } from "./shared";

// ── KPI Statistics ────────────────────────────────────────────────────────────

export const KPI_STATISTICS_QUERY = gql`
  query KpiStatistics(
    $from_ts: String!
    $to_ts: String!
    $tenant_ids: [Int!]
    $ongoing_events: Boolean! = true
    $anomalies: Boolean! = true
    $avg_detection_rate: Boolean! = true
    $avg_response_rate: Boolean! = true
  ) {
    kpi_statistics(from_ts: $from_ts, to_ts: $to_ts, tenant_ids: $tenant_ids) {
      ongoing_events @include(if: $ongoing_events)
      anomalies @include(if: $anomalies)
      avg_detection_rate @include(if: $avg_detection_rate)
      avg_response_rate @include(if: $avg_response_rate)
    }
  }
`;

export type KpiStatisticsResponse = {
  kpi_statistics: Partial<{
    ongoing_events: number;
    anomalies: number;
    avg_detection_rate: number;
    avg_response_rate: number;
  }>;
};

const KPI_DEFINITIONS = [
  { id: "ongoing-events", label: "Ongoing events", field: "ongoing_events", format: "number" },
  { id: "anomalies", label: "Active anomalies", field: "anomalies", format: "number" },
  {
    id: "avg-detection-rate",
    label: "Average detection rate",
    field: "avg_detection_rate",
    format: "percent",
  },
  {
    id: "avg-response-rate",
    label: "Average response rate",
    field: "avg_response_rate",
    format: "percent",
  },
] as const;

const KPI_SELECTION: SourceSelection = {
  mode: "select",
  fields: ["metrics", "freshness"],
  metrics: KPI_DEFINITIONS,
  toParams: ({ metricIds, fields, includeComparison }) => ({
    include_comparison: includeComparison ?? false,
    ...Object.fromEntries(
      KPI_DEFINITIONS.map(({ id, field }) => [
        field,
        fields?.includes("metrics") !== false &&
          (metricIds === undefined || metricIds.includes(id)),
      ]),
    ),
  }),
};

function buildKpiItems(summary: KpiStatisticsResponse["kpi_statistics"]): KpiStatisticsData {
  return {
    items: KPI_DEFINITIONS.flatMap(({ id, label, field, format }) => {
      const value = summary[field];
      return value === undefined
        ? []
        : [
            {
              id,
              label,
              value,
              format,
              tone:
                id === "anomalies"
                  ? value > 0
                    ? ("danger" as const)
                    : ("success" as const)
                  : ("neutral" as const),
              ...(format === "percent" ? { precision: 0 } : {}),
            },
          ];
    }),
  };
}

function normalizeKpiStatistics(raw: unknown): KpiStatisticsData {
  const data = raw as KpiStatisticsResponse;
  return buildKpiItems(data.kpi_statistics);
}

/**
 * The live KPI summary as a `report-metrics` collection, so `metric-kpi` works
 * out of the box in the widget catalog. It follows the Live Analytics tenant and
 * time-range controls, and its single period is the selected range.
 */
export function normalizeLiveKpiMetrics(
  raw: unknown,
  period: { from: string; to: string; label: string },
): ReportMetricsData {
  const reportPeriod = { key: "selected", ...period };
  const metrics: ReportMetric[] = buildKpiItems(
    (raw as KpiStatisticsResponse).kpi_statistics,
  ).items.map((item) => ({
    id: item.id,
    label: item.label,
    value: typeof item.value === "number" ? item.value : null,
    format: item.format === "percent" ? "percent" : "number",
    period: reportPeriod,
  }));
  return { metrics, freshness: null };
}

// ── Resolvers ─────────────────────────────────────────────────────────────────

export const kpiDataSources: Record<string, DataResolver> = {
  "kpi-statistics": {
    contract: { name: "kpi-statistics", version: 1 },
    selection: { mode: "complete", reason: "The fixed summary displays all four statistics." },
    buildParams: (_requestParams, filters) => ({
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      tenant_ids: getTenantIds(filters),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<KpiStatisticsResponse>(KPI_STATISTICS_QUERY, params);
      return normalizeKpiStatistics(raw);
    },
  },
  "live-kpi-metrics": {
    label: "Live analytics",
    description: "Follows the tenant and time range selected at the top of the dashboard.",
    contract: { name: "report-metrics", version: 1 },
    selection: KPI_SELECTION,
    buildParams: (requestParams, filters) => ({
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      period_label: filters.timeRange.label,
      tenant_ids: getTenantIds(filters),
      ...Object.fromEntries(
        [...KPI_DEFINITIONS.map(({ field }) => field), "include_comparison"].flatMap((field) =>
          typeof requestParams[field] === "boolean" ? [[field, requestParams[field]]] : [],
        ),
      ),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const { period_label, ...variables } = params;
      const raw = await client.request<KpiStatisticsResponse>(KPI_STATISTICS_QUERY, variables);
      return normalizeLiveKpiMetrics(raw, {
        from: String(variables.from_ts),
        to: String(variables.to_ts),
        label: String(period_label),
      });
    },
  },
};
